import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import type { ManualApprovalState } from '../types';
import type { BlackoutChecklistRow, BlackoutCoverageSummary } from '../utils';
import { FESTIVE_DATE_LABEL, formatContextDate } from '../utils';

import ApprovalStatusLabel from './ApprovalStatusLabel';
import EmptyTableRow from './EmptyTableRow';
import HiddenApprovedFooter from './HiddenApprovedFooter';
import ReadOnlyField from './ReadOnlyField';
import ReportCard from './ReportCard';
import ReportNote from './ReportNote';
import RuleComplianceIndicator from './RuleComplianceIndicator';
import SortTableCell from './SortTableCell';

interface Props {
  coverage: BlackoutCoverageSummary;
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<BlackoutChecklistRow>;
  showAutoApproved: boolean;
  travelFrom?: string;
  travelTo?: string;
  onApprovalChange: (rowId: string) => void;
}

const APPROVED_ROW_SX = {
  '& > td': {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.08),
  },
  '&:hover > td': {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.14),
  },
  '& .MuiInputBase-root': {
    bgcolor: 'background.paper',
  },
} as const;

export default function BlackoutReportCard({
  coverage,
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  travelFrom,
  travelTo,
  onApprovalChange,
}: Props) {
  return (
    <ReportCard>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <SortTableCell sx={{ width: 150 }}>Blackout Name</SortTableCell>
              <SortTableCell sx={{ width: 170 }}>From</SortTableCell>
              <SortTableCell sx={{ width: 170 }}>To</SortTableCell>
              <SortTableCell sx={{ width: 170 }} labelSx={{ whiteSpace: 'normal' }}>
                Within Travel Validity
              </SortTableCell>
              <SortTableCell sx={{ width: 230 }} labelSx={{ whiteSpace: 'normal' }}>
                Clash with Gala / Festive Dates
              </SortTableCell>
              <TableCell sx={{ width: 110 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={6} label="No blackout rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = !!manualApprovals[row.id];
                const approved = row.status === 'approved' || manuallyApproved;

                return (
                  <TableRow key={row.id} hover sx={approved ? APPROVED_ROW_SX : undefined}>
                    <TableCell>
                      <ReadOnlyField value={row.name} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={formatContextDate(row.from)} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={formatContextDate(row.to)} />
                    </TableCell>
                    <TableCell>
                      <RuleComplianceIndicator
                        isCompliant={row.withinTravelValidity}
                        label={row.withinTravelValidity ? 'Yes' : 'No'}
                      />
                    </TableCell>
                    <TableCell>
                      <RuleComplianceIndicator isCompliant={row.festiveClear} label={row.festiveClear ? 'No' : 'Yes'} />
                    </TableCell>
                    <TableCell>
                      {approved ? (
                        <ApprovalStatusLabel manuallyApproved={manuallyApproved} />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => onApprovalChange(row.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        sx={{ borderBottom: 0, borderTop: 1 }}
        text={
          <>
            <Box>
              <strong>Blackout coverage rule:</strong> cumulative blackout dates must cover less than{' '}
              <strong>75%</strong> of the travel period.
            </Box>
            <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2 }}>
              <li>
                <strong>Travel dates:</strong> {formatContextDate(travelFrom)} - {formatContextDate(travelTo)}
                {coverage.travelDays ? ` (${coverage.travelDays} total days)` : ''}
              </li>
              <li>
                <strong>Total blackout days:</strong> {coverage.blackoutDays} ({coverage.percent}% of travel period)
                <Box
                  component="span"
                  aria-label={
                    coverage.status === 'approved'
                      ? 'Total blackout percentage is within range'
                      : 'Total blackout percentage is outside range'
                  }
                  title={
                    coverage.status === 'approved'
                      ? 'Total blackout percentage is within range'
                      : 'Total blackout percentage is outside range'
                  }
                  sx={{
                    bgcolor: coverage.status === 'approved' ? 'success.main' : 'warning.main',
                    borderRadius: '50%',
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.04)',
                    display: 'inline-block',
                    height: 10,
                    ml: 1,
                    verticalAlign: '-1px',
                    width: 10,
                  }}
                />
              </li>
              <li>
                <strong>Festive dates checked:</strong> {FESTIVE_DATE_LABEL}. Rows that overlap any of these dates
                require approval.
              </li>
            </Box>
          </>
        }
      />
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="blackout row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
